import { isEmpty, isNil, isNull } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Easing, TouchableOpacity } from 'react-native';
import { useDebounce } from 'use-debounce';
import { MBView } from '@shared/components/MBView';
import { RCSubMenu, RCSubMenuProps } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBAnimation } from '@shared/components/animation';
import { Dropdown } from '@shared/components/dropdown';
import { MBHover } from '@shared/components/hover';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextBody2, MBTextOverlineBold } from '@shared/components/typography';
import { useGetWorkflowTemplateAutomations } from '@shared/scenes/automation/hooks/useGetWorkflowTemplateAutomations';
import { ScheduledAutomationComponent } from '@shared/scenes/workflows/config';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { Divider } from '@shared/scenes/workflows/edit/components/optionsMenu/divider';
import {
  ListItem,
  SelectableItemList,
} from '@shared/scenes/workflows/edit/components/optionsMenu/referencedInOptions/SelectableItemList';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isDateComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, Colors, cursorPointerStyle, grid } from '@shared/styles';
import {
  DEFAULT_COUNT,
  DEFAULT_COUNT_STR,
  TIME_OF_EVENT_DURATION,
  dedupeDurations,
  parseDurationString,
  unitListItems,
} from '@shared/util/durations';
import { I18nKey, i18n } from '@shared/util/i18n';

import { NestedOptionsRow } from '../NestedOptionsRow';
import { MBWorkflowDefaultComponentOptions } from '../defaultOptions';
import { UnitMenu } from './UnitMenu';
import styles from './styles.module.css';

interface ScheduledAutomationOptionsProps {
  component: ScheduledAutomationComponent;
  components: MBAPI.WorkflowTemplate_components[];
  workflowTemplateId: string;
}

export function ScheduledAutomationOptions({
  component,
  components,
  workflowTemplateId,
}: ScheduledAutomationOptionsProps) {
  const colors = useContext(ColorThemeContext);
  const dispatch = useWorkflowDispatch();
  const dateComponents = components?.filter(isDateComponent);
  const dateComponentsListItems: ListItem[] = dateComponents?.map(({ id, label, icon }) => {
    return { id, label: label ?? '', icon: icon as MBIconV2Name };
  });
  const selectedDateComponent = dateComponents?.find(dc => dc.id === component.dateComponentId);
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const componentWasSavedPreviously = !isNil(
    workflowTemplate?.components?.find(c => c.id === component.id),
  );
  const allowDateComponentChange = !componentWasSavedPreviously || isNil(component.dateComponentId);
  const allowAutomationChange = !componentWasSavedPreviously || isNil(component.automationId);

  const [_query, setQuery] = useState('');
  const [query] = useDebounce(_query, 300);
  const {
    automations: adHocAutomations,
    loading: loadingAutomations,
    hasMore: hasMoreAutomations,
    fetchMore: loadMoreAutomations,
  } = useGetWorkflowTemplateAutomations({
    workflowTemplateId,
    name: isEmpty(query) ? undefined : query,
    trigger: 'ADHOC',
  });
  const adHocAutomationsListItems: ListItem[] = adHocAutomations?.map(({ id, name }) => {
    return { id, label: name, icon: 'lightning-bolt' as MBIconV2Name };
  });
  const selectedAutomation = adHocAutomations?.find(a => a.id === component.automationId);

  const setDateComponent = useCallback(
    (dateComponentId: string) => {
      const dateComponent = dateComponents?.find(dc => dc.id === dateComponentId);
      const isDefaultLabel =
        component.label === i18n('workflowTemplate.component.type.label.scheduledAutomation');
      const isDefaultPlaceholder =
        component.placeholder === i18n('workflowTemplate.component.type.label.scheduledAutomation');

      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          label: isDefaultLabel
            ? generateDefaultLabel(dateComponent, selectedAutomation) || component.label
            : component.label,
          placeholder: isDefaultPlaceholder
            ? generateDefaultLabel(dateComponent, selectedAutomation) || component.placeholder
            : component.placeholder,
          dateComponentId,
        },
      });
    },
    [dispatch, component, dateComponents, selectedAutomation],
  );

  const setAutomation = useCallback(
    (automationId: string) => {
      const automation = adHocAutomations?.find(a => a.id === automationId);
      const isDefaultLabel =
        component.label === i18n('workflowTemplate.component.type.label.scheduledAutomation');
      const isDefaultPlaceholder =
        component.placeholder === i18n('workflowTemplate.component.type.label.scheduledAutomation');

      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          label: isDefaultLabel
            ? generateDefaultLabel(selectedDateComponent, automation) || component.label
            : component.label,
          placeholder: isDefaultPlaceholder
            ? generateDefaultLabel(selectedDateComponent, automation) || component.placeholder
            : component.placeholder,
          automationId,
        },
      });
    },
    [dispatch, component, adHocAutomations, selectedDateComponent],
  );

  const setDefault = useCallback(
    (defaultValues: string[]) => {
      dispatch({
        type: 'updateComponent',
        args: {
          ...component,
          default: {
            automationTriggerDurations: defaultValues,
          },
        } as ScheduledAutomationComponent,
      });
    },
    [dispatch, component],
  );

  const handleDedupeDefaults = useCallback(() => {
    const dedupedDefaults = dedupeDurations(component.default?.automationTriggerDurations ?? []);

    dispatch({
      type: 'updateComponent',
      args: {
        ...component,
        default: {
          automationTriggerDurations: dedupedDefaults,
        },
      } as ScheduledAutomationComponent,
    });
  }, [dispatch, component]);

  const handleMenuVisibleChange = (openKeys: string[], previousOpenKeys: string[]) => {
    if (
      previousOpenKeys.includes('scheduledAutomationDefaultOptions') &&
      !openKeys.includes('scheduledAutomationDefaultOptions')
    ) {
      handleDedupeDefaults();
    }
  };

  return (
    <MBWorkflowDefaultComponentOptions
      component={component}
      onMenusVisibleChange={handleMenuVisibleChange}
      extraSections={() => (
        <>
          <View paddingHorizontal={3} paddingVertical={1}>
            <MBTextOverlineBold color={colors.foregroundInactive}>
              {i18n('builder.scheduledAutomation.fieldsLabel')}
            </MBTextOverlineBold>
          </View>
          <RCSubMenu
            key="scheduledAutomationDateOptions"
            style={{
              zIndex: 999,
              opacity: isEmpty(dateComponents) ? 0.5 : 1,
              cursor: !allowDateComponentChange || isEmpty(dateComponents) ? 'default' : 'pointer',
            }}
            disabled={!allowDateComponentChange || isEmpty(dateComponents)}
            title={
              <NestedOptionsRow
                name={
                  isNil(selectedDateComponent)
                    ? i18n('builder.scheduledAutomation.selectDate')
                    : selectedDateComponent.label ?? ''
                }
                defaultIcon={
                  isNil(selectedDateComponent)
                    ? 'calendar'
                    : (selectedDateComponent.icon as MBIconV2Name)
                }
                selectedIcon={
                  isNil(selectedDateComponent)
                    ? 'calendar'
                    : (selectedDateComponent.icon as MBIconV2Name)
                }
                enabled={allowDateComponentChange}
                isBoxedSelectedIcon={false}
              />
            }
          >
            <View paddingVertical={1}>
              <SelectableItemList
                items={dateComponentsListItems}
                onSelectItem={setDateComponent}
                enabled={true}
                selectedItem={component.dateComponentId}
                label={i18n('builder.scheduledAutomation.dateListLabel')}
                helpText={i18n('builder.scheduledAutomation.dateListHelpText')}
              />
            </View>
          </RCSubMenu>
          <RCSubMenu
            key="scheduledAutomationAutomationOptions"
            style={{
              zIndex: 999,
              opacity: isEmpty(adHocAutomations) ? 0.5 : 1,
              cursor: !allowAutomationChange || isEmpty(adHocAutomations) ? 'default' : 'pointer',
            }}
            disabled={!allowAutomationChange || isEmpty(adHocAutomations)}
            title={
              <NestedOptionsRow
                name={
                  isNil(selectedAutomation)
                    ? i18n('builder.scheduledAutomation.selectAutomation')
                    : selectedAutomation.name
                }
                defaultIcon="lightning-bolt"
                selectedIcon="lightning-bolt"
                enabled={allowAutomationChange}
                isBoxedSelectedIcon={false}
              />
            }
          >
            <View paddingVertical={1}>
              <SelectableItemList
                items={adHocAutomationsListItems}
                onSelectItem={setAutomation}
                enabled={true}
                selectedItem={component.automationId ?? undefined}
                query={query}
                setQuery={setQuery}
                loading={loadingAutomations}
                hasMore={hasMoreAutomations}
                loadMore={loadMoreAutomations}
                label={i18n('builder.scheduledAutomation.automationListLabel')}
                helpText={i18n('builder.scheduledAutomation.automationListHelpText')}
              />
            </View>
          </RCSubMenu>
          <View paddingHorizontal={2} paddingTop={1}>
            <Divider />
          </View>
          <View paddingHorizontal={3} paddingVertical={1}>
            <MBTextOverlineBold color={colors.foregroundInactive}>
              {i18n('builder.scheduledAutomation.defaultOptionsLabel')}
            </MBTextOverlineBold>
          </View>
          <RCSubMenu
            key="scheduledAutomationDefaultOptions"
            placement="rightBottom"
            popupClassName={styles.defaultOptionsMenu}
            style={{ zIndex: 999 }}
            title={
              <NestedOptionsRow
                name={i18n('builder.scheduledAutomation.selectDefault')}
                defaultIcon="time-clock"
                selectedIcon="time-clock"
              />
            }
          >
            <DefaultOptions component={component} setDefault={setDefault} />
          </RCSubMenu>
          <View paddingHorizontal={2} paddingTop={1}>
            <Divider />
          </View>
        </>
      )}
    />
  );
}

function DefaultOptions({
  component,
  setDefault = () => {},
  ...subMenuProps
}: {
  component: ScheduledAutomationComponent;
  setDefault?: (defaultValues: string[]) => void;
}) {
  const defaults = useMemo(
    () => [...(component.default?.automationTriggerDurations ?? [])],
    [component.default?.automationTriggerDurations],
  );

  const handleSetDuration = useCallback(
    (duration: string | null, index: number) => {
      const newDefaults = [...defaults];
      if (isNull(duration)) {
        newDefaults.splice(index, 1);
      } else {
        newDefaults[index] = duration;
      }
      setDefault(newDefaults);
    },
    [defaults, setDefault],
  );

  const allowAdd = isEmpty(defaults) || !defaults.some(d => isEmpty(d));
  const colors = useContext(ColorThemeContext);
  const [addMenuVisible, setAddMenuVisible] = useState(false);

  return (
    <>
      <View style={{ width: 278, maxHeight: 400, overflow: 'scroll' }} paddingVertical={1}>
        <View paddingHorizontal={2} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('builder.scheduledAutomation.defaultListLabel')}
          </MBTextOverlineBold>
        </View>
        <View paddingHorizontal={2} paddingVertical={1} style={{ maxWidth: 278 }}>
          <MBTextBody2 color={colors.foregroundInactive}>
            {i18n('builder.scheduledAutomation.defaultListHelpText')}
          </MBTextBody2>
        </View>
      </View>
      {!isEmpty(defaults) &&
        defaults.map((duration, i) => (
          <Duration
            {...subMenuProps}
            key={i}
            duration={duration}
            durationIndex={i}
            onChangeDuration={handleSetDuration}
          />
        ))}
      {allowAdd && (
        <Dropdown
          closeOnSelect
          allowOutsideClick
          style={{ padding: 0 }}
          visible={addMenuVisible}
          setVisible={setAddMenuVisible}
          button={
            <TouchableOpacity onPress={() => setAddMenuVisible(true)}>
              <MBHover
                hoverStyle={{ backgroundColor: colors.input.backgroundInactive }}
                style={{ marginVertical: grid(1) }}
              >
                <MBView
                  paddingHorizontal={2}
                  paddingVertical={1}
                  style={[
                    {
                      flexDirection: 'row',
                      alignItems: 'center',
                    },
                    cursorPointerStyle,
                  ]}
                >
                  <MBIconV2
                    name="plus"
                    color={colors.foregroundActive}
                    size={grid(3)}
                    style={{ marginRight: grid(1) }}
                  />
                  <MBTextBody1>
                    {i18n('builder.scheduledAutomation.defaultAddDuration')}
                  </MBTextBody1>
                </MBView>
              </MBHover>
            </TouchableOpacity>
          }
        >
          <UnitMenu
            onUnitSelect={u => {
              setAddMenuVisible(false);
              handleSetDuration(u.replace(/n/, DEFAULT_COUNT_STR), defaults.length);
            }}
          />
        </Dropdown>
      )}
    </>
  );
}

function Duration({
  duration,
  durationIndex = 0,
  onChangeDuration,
  ...subMenuProps
}: {
  duration?: string;
  durationIndex?: number;
  onChangeDuration: (duration: string | null, index: number) => void;
} & RCSubMenuProps) {
  const colors = useContext(ColorThemeContext);
  const parsedDuration = useMemo(() => parseDurationString(duration), [duration]);
  const [number, setNumber] = useState(parsedDuration?.number || '');
  const [unit, setUnit] = useState(parsedDuration?.unit || '');
  const showInput = duration !== TIME_OF_EVENT_DURATION;

  useEffect(() => {
    setNumber(parsedDuration?.number || '');
    setUnit(parsedDuration?.unit || '');
  }, [parsedDuration]);

  const updateDefault = useCallback(
    (numberValue?, unitValue?) => {
      const newNumber = numberValue ?? number ?? '';
      const newUnit = unitValue ?? unit ?? '';

      if (!isEmpty(newNumber) && !isEmpty(newUnit)) {
        onChangeDuration(newUnit.replace('n', newNumber), durationIndex);
      }
    },
    [number, unit, onChangeDuration, durationIndex],
  );

  const handleRemove = () => {
    onChangeDuration(null, durationIndex);
  };

  const handleUnitSelect = (selectedUnit: string) => {
    setMenuVisible(false);
    setUnit(selectedUnit);
    updateDefault(number, selectedUnit);
  };

  const handleInputChange = (numberValue: string) => {
    setNumber(numberValue);
    updateDefault(numberValue, unit);
  };

  const [menuVisible, setMenuVisible] = useState(false);

  return (
    <View row centerVertically paddingVertical={0.5} paddingHorizontal={2}>
      {showInput && (
        <MBTextInput
          value={number}
          onChangeText={handleInputChange}
          style={{
            width: grid(6),
            height: grid(5) - 2,
            backgroundColor: Colors.v2.white,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: Colors.v2.gray10,
            marginRight: grid(1),
            zIndex: 999,
          }}
        />
      )}
      <Dropdown
        closeOnSelect
        allowOutsideClick
        visible={menuVisible}
        setVisible={setMenuVisible}
        style={{ padding: 0 }}
        button={
          <TouchableOpacity onPress={() => setMenuVisible(true)}>
            <MBHover
              hoverStyle={{
                backgroundColor: colors.input.backgroundInactive,
                borderRadius: grid(0.5),
              }}
            >
              <MBView
                paddingHorizontal={2}
                paddingVertical={1}
                style={[
                  {
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: Colors.v2.gray10,
                    borderRadius: grid(0.5),
                  },
                  cursorPointerStyle,
                ]}
              >
                <View marginRight={2}>
                  <MBTextBody1>
                    {i18n((unitListItems.find(i => i.id === unit)?.label ?? '') as I18nKey, {
                      count: !isEmpty(number) ? Number(number) : 0,
                    })}
                  </MBTextBody1>
                </View>
                <MBAnimation
                  duration={200}
                  easing={Easing.inOut(Easing.quad)}
                  disabled={
                    !subMenuProps.openKeys?.includes(
                      `scheduledAutomationDefaultOption${durationIndex}`,
                    )
                  }
                  rotate={{ from: '0deg', to: '-180deg' }}
                >
                  <MBIconV2
                    color={Colors.v2.purple}
                    name="arrow-point-down-heavy"
                    size={grid(1.25)}
                  />
                </MBAnimation>
              </MBView>
            </MBHover>
          </TouchableOpacity>
        }
      >
        <UnitMenu
          count={!isEmpty(number) ? Number(number) : DEFAULT_COUNT}
          onUnitSelect={handleUnitSelect}
        />
      </Dropdown>
      <MBTouchableOpacity onPress={handleRemove} style={{ marginLeft: grid(2) }}>
        <MBIconV2 name="trash" color={colors.foregroundActive} size={grid(3)} />
      </MBTouchableOpacity>
    </View>
  );
}

function generateDefaultLabel(
  selectedDateComponent?: MBAPI.WorkflowTemplate_components_DateComponent,
  selectedAutomation?: MBAPI.Automation,
): string {
  if (isNil(selectedDateComponent) || isNil(selectedAutomation)) {
    return '';
  }

  const hasDateLabel = !isNil(selectedDateComponent.label);
  const hasAutomationName = !isNil(selectedAutomation.name);

  if (hasDateLabel && hasAutomationName) {
    return `${selectedAutomation.name} (based on ${selectedDateComponent.label})`;
  }
  if (hasDateLabel && !hasAutomationName) {
    return `${i18n('workflowTemplate.component.type.label.scheduledAutomation')} (based on ${
      selectedDateComponent.label
    })`;
  }
  if (!hasDateLabel && hasAutomationName) {
    return `${selectedAutomation.name} (based on date)`;
  }
  return '';
}
